import { Injectable } from '@angular/core';
import { CloudConnectStateService } from '@vex/constants/cloud-connect-state';
import {
  CredentialReferenceType,
  EnvironmentType
} from '@vex/model/dependency-map';

/**
 * Centralizes the temporary polymorphic AccountId contract.
 *
 * New records carry CredentialReferenceType from their Dependency Map. Environment-based inference remains
 * only as a bounded fallback for records created before that metadata was materialized.
 */
@Injectable({ providedIn: 'root' })
export class CredentialReferenceService {
  constructor(private cloudState: CloudConnectStateService) {}

  resolve(source: any, dependencyMapName?: string): CredentialReferenceType {
    // Skip legacy Unspecified values instead of allowing them to mask a classified embedded Blueprint or map.
    const explicit = [
      source?.CredentialReferenceType,
      source?.Blueprint?.CredentialReferenceType,
      this.cloudState.getCloud(dependencyMapName)?.CredentialReferenceType
    ].find(
      value =>
        value === CredentialReferenceType.Account ||
        value === CredentialReferenceType.DeviceAuthentication
    );

    if (
      explicit === CredentialReferenceType.Account ||
      explicit === CredentialReferenceType.DeviceAuthentication
    ) {
      return explicit;
    }

    const environment =
      source?.EnvironmentType ??
      source?.Blueprint?.EnvironmentType ??
      this.cloudState.getEnvironmentType(dependencyMapName);
    return environment === EnvironmentType.DataCenter
      ? CredentialReferenceType.DeviceAuthentication
      : CredentialReferenceType.Account;
  }

  usesAccount(source: any, dependencyMapName?: string): boolean {
    return this.resolve(source, dependencyMapName) === CredentialReferenceType.Account;
  }

  label(source: any, dependencyMapName?: string): string {
    return this.usesAccount(source, dependencyMapName)
      ? 'Provider or controller account'
      : 'Device authentication profile';
  }

  valueLabel(source: any, dependencyMapName?: string): string {
    return this.usesAccount(source, dependencyMapName)
      ? 'Account Name'
      : 'Device Authentication';
  }
}
